// RightSideNav.jsx
import React, { useEffect, useMemo } from "react";
import { Select, Spin } from "antd";
import { DownOutlined } from "@ant-design/icons";
import { useSelection } from "./SelectionContext";
import { useAuth } from "./context/AuthContext";
import ThemeToggle from "./components/ThemeToggle";

const { Option } = Select;

const normalizeSearchText = (value = "") =>
  String(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const filterSelectOption = (input, option) => {
  const optionLabel =
    typeof option?.children === "string" ? option.children : option?.label || "";

  return normalizeSearchText(optionLabel).includes(normalizeSearchText(input));
};

const RightSideNav = () => {
  const {
    selectedCompany,
    setSelectedCompany,
    selectedWarehouse,
    setSelectedWarehouse,
  } = useSelection();

  const { user, isLoading: authLoading } = useAuth();

  const assignedWarehouses = useMemo(() => {
    return user && Array.isArray(user.assigned_warehouses)
      ? user.assigned_warehouses
      : [];
  }, [user]);

  const availableCompanies = useMemo(() => {
    if (!assignedWarehouses || assignedWarehouses.length === 0) return [];

    const companyMap = new Map();
    assignedWarehouses.forEach((wh) => {
      if (wh.company_id && !companyMap.has(wh.company_id)) {
        companyMap.set(wh.company_id, {
          id: wh.company_id,
          name: wh.company_name || `Entreprise ID ${wh.company_id}`,
        });
      }
    });

    return Array.from(companyMap.values());
  }, [assignedWarehouses]);

  const warehousesForSelectedCompany = useMemo(() => {
    if (!selectedCompany || !assignedWarehouses) return [];
    return assignedWarehouses.filter((wh) => wh.company_id === selectedCompany);
  }, [selectedCompany, assignedWarehouses]);

  useEffect(() => {
    if (authLoading || !user) return;

    if (assignedWarehouses.length === 0) {
      if (selectedCompany) setSelectedCompany(null);
      if (selectedWarehouse) setSelectedWarehouse(null);
      return;
    }

    const companyIsValid = availableCompanies.some(
      (company) => company.id === selectedCompany
    );

    if (selectedCompany && !companyIsValid) {
      setSelectedCompany(null);
      setSelectedWarehouse(null);
      return;
    }

    if (availableCompanies.length === 1 && !selectedCompany) {
      setSelectedCompany(availableCompanies[0].id);
    }

    if (selectedCompany && selectedWarehouse) {
      const warehouseIsValid = warehousesForSelectedCompany.some(
        (warehouse) => warehouse.id === selectedWarehouse
      );

      if (!warehouseIsValid) {
        setSelectedWarehouse(null);
      }
    }

    if (
      selectedCompany &&
      warehousesForSelectedCompany.length === 1 &&
      !selectedWarehouse
    ) {
      setSelectedWarehouse(warehousesForSelectedCompany[0].id);
    }
  }, [
    authLoading,
    user,
    assignedWarehouses,
    availableCompanies,
    selectedCompany,
    selectedWarehouse,
    setSelectedCompany,
    setSelectedWarehouse,
    warehousesForSelectedCompany,
  ]);

  const handleCompanyChange = (value) => {
    const companyId = value ? Number(value) : null;

    if (companyId !== selectedCompany) {
      setSelectedCompany(companyId);
      setSelectedWarehouse(null);
    }
  };

  const handleWarehouseChange = (value) => {
    const warehouseId = value ? Number(value) : null;

    if (warehouseId !== selectedWarehouse) {
      setSelectedWarehouse(warehouseId);
    }
  };

  if (authLoading) {
    return (
      <div className="right-nav right-nav--loading">
        <div className="right-nav__status">
          <Spin size="small" />
          <span>Chargement...</span>
        </div>
      </div>
    );
  }

  if (!user || assignedWarehouses.length === 0) {
    return (
      <div className="right-nav">
        <div className="right-nav__content">
          <Select
            placeholder="Aucune entreprise"
            className="right-nav__select"
            disabled
          />
          <Select
            placeholder="Aucun établissement"
            className="right-nav__select"
            disabled
          />
          <div className="right-nav__theme">
            <ThemeToggle
              size="middle"
              className="right-nav__theme-toggle"
              showLabel={false}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="right-nav">
      <div className="right-nav__content">
        <Select
          placeholder="Entreprise"
          className="right-nav__select"
          value={selectedCompany || undefined}
          onChange={handleCompanyChange}
          showSearch
          allowClear
          suffixIcon={<DownOutlined />}
          disabled={availableCompanies.length === 0}
          loading={authLoading}
          optionFilterProp="children"
          filterOption={filterSelectOption}
          notFoundContent="Aucun résultat"
        >
          {availableCompanies.map((company) => (
            <Option key={company.id} value={company.id}>
              {company.name}
            </Option>
          ))}
        </Select>

        <Select
          placeholder="Établissement"
          className="right-nav__select"
          value={selectedWarehouse || undefined}
          onChange={handleWarehouseChange}
          disabled={
            !selectedCompany || warehousesForSelectedCompany.length === 0
          }
          showSearch
          allowClear
          suffixIcon={<DownOutlined />}
          loading={authLoading}
          optionFilterProp="children"
          filterOption={filterSelectOption}
          notFoundContent="Aucun résultat"
        >
          {warehousesForSelectedCompany.map((warehouse) => (
            <Option key={warehouse.id} value={warehouse.id}>
              {warehouse.name}
            </Option>
          ))}
        </Select>

        <div className="right-nav__theme">
          <ThemeToggle
            size="middle"
            className="right-nav__theme-toggle"
            showLabel={false}
          />
        </div>
      </div>
    </div>
  );
};

export default RightSideNav;
